import { ContextMenuAction } from '../../explorer/models/TreeModel';
import type { FolderTreeNode } from '../../explorer/models/DatabaseTreeModels';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { toast } from '../../../components/ui/toaster';
import type { Connection } from '../../../types/electron';
import type { SchemaFilterMode } from '../../workspace/core/types/workspaceTypes';
import { isBetaFeatureEnabled } from '../../../core/beta';
import { safeConnectionAlias, safeConnectionName, safeConnectionServer } from '../../../types/safeConnectionDisplay';

/**
 * Folder-specific context menu action types
 */
type FolderContextMenuActionType =
  | 'refresh'
  | 'tables-overview'
  | 'views-overview'
  | 'procedures-overview'
  | 'functions-overview'
  | 'package-explorer'
  | 'script-new-table'
  | 'script-new-view'
  | 'script-new-procedure'
  | 'script-new-function';

const FolderActions = {
  REFRESH: 'refresh' as FolderContextMenuActionType,
  TABLES_OVERVIEW: 'tables-overview' as FolderContextMenuActionType,
  VIEWS_OVERVIEW: 'views-overview' as FolderContextMenuActionType,
  PROCEDURES_OVERVIEW: 'procedures-overview' as FolderContextMenuActionType,
  FUNCTIONS_OVERVIEW: 'functions-overview' as FolderContextMenuActionType,
  PACKAGE_EXPLORER: 'package-explorer' as FolderContextMenuActionType,
  SCRIPT_NEW_TABLE: 'script-new-table' as FolderContextMenuActionType,
  SCRIPT_NEW_VIEW: 'script-new-view' as FolderContextMenuActionType,
  SCRIPT_NEW_PROCEDURE: 'script-new-procedure' as FolderContextMenuActionType,
  SCRIPT_NEW_FUNCTION: 'script-new-function' as FolderContextMenuActionType,
};

/**
 * Create a separator item
 */
function createSeparator(): ContextMenuAction {
  return {
    id: 'separator',
    label: '',
    isSeparator: true,
  };
}

/**
 * Create a refresh action
 */
function createRefreshAction(): ContextMenuAction {
  return {
    id: FolderActions.REFRESH,
    label: 'Refresh',
    icon: 'refresh',
  };
}

/**
 * Create Tables Overview action
 */
function createTablesOverviewAction(): ContextMenuAction {
  return {
    id: FolderActions.TABLES_OVERVIEW,
    label: 'Tables Overview',
    icon: 'network',
  };
}

/**
 * Create Views Overview action
 */
function createViewsOverviewAction(): ContextMenuAction {
  return {
    id: FolderActions.VIEWS_OVERVIEW,
    label: 'Views Overview',
    icon: 'network',
  };
}

/**
 * Create Procedures Overview action
 */
function createProceduresOverviewAction(): ContextMenuAction {
  return {
    id: FolderActions.PROCEDURES_OVERVIEW,
    label: 'Procedures Overview',
    icon: 'network',
  };
}

/**
 * Create Functions Overview action
 */
function createFunctionsOverviewAction(): ContextMenuAction {
  return {
    id: FolderActions.FUNCTIONS_OVERVIEW,
    label: 'Functions Overview',
    icon: 'network',
  };
}

/**
 * Generate context menu actions for tables folder
 */
export function generateTablesFolderContextMenuActions(): ContextMenuAction[] {
  return [
    createTablesOverviewAction(),
    createSeparator(),
    {
      id: FolderActions.SCRIPT_NEW_TABLE,
      label: 'Script New Table...',
      icon: 'file-plus',
    },
    createSeparator(),
    createRefreshAction(),
  ];
}

/**
 * Generate context menu actions for views folder
 */
export function generateViewsFolderContextMenuActions(): ContextMenuAction[] {
  return [
    createViewsOverviewAction(),
    createSeparator(),
    {
      id: FolderActions.SCRIPT_NEW_VIEW,
      label: 'Script New View...',
      icon: 'file-plus',
    },
    createSeparator(),
    createRefreshAction(),
  ];
}

/**
 * Generate context menu actions for stored procedures folder
 */
export function generateStoredProceduresFolderContextMenuActions(): ContextMenuAction[] {
  return [
    createProceduresOverviewAction(),
    createSeparator(),
    {
      id: FolderActions.SCRIPT_NEW_PROCEDURE,
      label: 'Script New Stored Procedure...',
      icon: 'file-plus',
    },
    createSeparator(),
    createRefreshAction(),
  ];
}

/**
 * Generate context menu actions for functions folder
 */
export function generateFunctionsFolderContextMenuActions(): ContextMenuAction[] {
  return [
    createFunctionsOverviewAction(),
    createSeparator(),
    {
      id: FolderActions.SCRIPT_NEW_FUNCTION,
      label: 'Script New Function...',
      icon: 'file-plus',
    },
    createSeparator(),
    createRefreshAction(),
  ];
}

/**
 * Generate context menu actions for packages folder
 */
export function generatePackagesFolderContextMenuActions(): ContextMenuAction[] {
  return [
    // Package Explorer is an opt-in beta surface; scripting a new package is
    // not, so the folder menu keeps working with the entry removed.
    ...(isBetaFeatureEnabled('package-explorer')
      ? [
          {
            id: FolderActions.PACKAGE_EXPLORER,
            label: 'Package Explorer',
            icon: 'package',
          } satisfies ContextMenuAction,
          createSeparator(),
        ]
      : []),
    {
      id: 'script-new-package',
      label: 'Script New Package...',
      icon: 'file-plus',
    },
    createSeparator(),
    createRefreshAction(),
  ];
}

/**
 * Get connection configuration by ID
 */
function getConnectionConfig(connectionId: string): Connection | undefined {
  const { connections, activeConnections } = useConnectionStore.getState();
  const saved = connections.find((conn) => conn.id === connectionId);
  if (saved) return saved;
  return activeConnections.find((conn) => conn.id === connectionId)?.connection;
}

/**
 * Get filter mode label for tab title
 */
function getFilterModeLabel(filterMode: SchemaFilterMode): string {
  const labels: Record<SchemaFilterMode, string> = {
    all: 'Overview',
    tables: 'Tables',
    views: 'Views',
    procedures: 'Procedures',
    functions: 'Functions',
  };
  return labels[filterMode];
}

/**
 * Open Schema Overview with filter
 */
function openFilteredSchemaOverview(
  connectionId: string,
  databaseName: string,
  filterMode: SchemaFilterMode
): void {
  const connection = getConnectionConfig(connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const { addTab } = useWorkspaceStore.getState();
  const label = getFilterModeLabel(filterMode);

  addTab({
    title: `${label}: ${databaseName}`,
    type: 'schema-overview',
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionName(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(connection),
      database: databaseName,
    },
    content: {
      connectionId,
      databaseName,
      filterMode,
      isLoading: true,
    },
  });
}

/**
 * Execute a folder context menu action
 */
export function executeFolderContextMenuAction(
  actionId: string,
  nodeId: string,
  handlers: {
    onRefresh?: (nodeId: string) => void;
  } = {},
  node?: FolderTreeNode
): void {
  // Handle refresh separately
  if (actionId === FolderActions.REFRESH) {
    handlers.onRefresh?.(nodeId);
    return;
  }

  if (!node) {
    toast.error({ title: 'Error', description: 'Folder node not found' });
    return;
  }

  // Extract metadata - all folder types have connectionId and database
  const metadata = node.metadata;
  const connectionId = 'connectionId' in metadata ? metadata.connectionId : undefined;
  const databaseName = 'database' in metadata ? metadata.database : undefined;

  if (!connectionId || !databaseName) {
    toast.error({ title: 'Error', description: 'Connection or database not found' });
    return;
  }

  switch (actionId) {
    case FolderActions.TABLES_OVERVIEW:
      openFilteredSchemaOverview(connectionId, databaseName, 'tables');
      break;

    case FolderActions.VIEWS_OVERVIEW:
      openFilteredSchemaOverview(connectionId, databaseName, 'views');
      break;

    case FolderActions.PROCEDURES_OVERVIEW:
      openFilteredSchemaOverview(connectionId, databaseName, 'procedures');
      break;

    case FolderActions.FUNCTIONS_OVERVIEW:
      openFilteredSchemaOverview(connectionId, databaseName, 'functions');
      break;

    case FolderActions.PACKAGE_EXPLORER:
      void (async () => {
        const { findExistingPackageExplorerTab, createDefaultPackageExplorerContent } =
          await import('../../workspace/package-explorer/types');
        const { tabs, addTab, setActiveTab } = useWorkspaceStore.getState();
        const existing = findExistingPackageExplorerTab(tabs, connectionId, databaseName);
        if (existing) {
          setActiveTab(existing.id);
        } else {
          const connConfig = getConnectionConfig(connectionId);
          addTab({
            title: `Package Explorer — ${databaseName}`,
            type: 'package-explorer',
            connectionInfo: {
              connectionId,
              connectionName: safeConnectionAlias(connConfig, 'Unnamed Connection'),
              serverHost: safeConnectionServer(connConfig),
              database: databaseName,
            },
            content: createDefaultPackageExplorerContent({ connectionId, database: databaseName }),
          });
        }
      })();
      break;

    // Script New actions are handled by the main contextMenuService
    // (they open a new query tab with template script)
    default:
      break;
  }
}
